import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useToast } from '@/hooks/useToast';
import { Zap, Send } from 'lucide-react';

interface ShareToNostrDialogProps {
  pollQuestion: string;
  pollUrl: string;
  disabled?: boolean;
}

export function ShareToNostrDialog({ pollQuestion, pollUrl, disabled }: ShareToNostrDialogProps) {
  const { user } = useCurrentUser();
  const { mutate: publishNote, isPending } = useNostrPublish();
  const { toast } = useToast();

  const [open, setOpen] = useState(false);
  const defaultContent = `Cast Your Vote! 🔥\n\n${pollQuestion}\n\n${pollUrl}`;
  const [content, setContent] = useState(defaultContent);

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (newOpen) {
      // Reset content when opening
      setContent(defaultContent);
    }
  };

  const handlePublish = () => {
    if (!user) {
      toast({
        title: 'Login required',
        description: 'Please login to share to Nostr',
        variant: 'destructive',
      });
      return;
    }

    publishNote(
      {
        kind: 1,
        content: content.trim(),
        tags: [],
      },
      {
        onSuccess: () => {
          toast({
            title: '🔥 Shared to Nostr!',
            description: 'Your note has been published to the network',
          });
          setOpen(false);
        },
        onError: (error) => {
          console.error('Share to Nostr failed:', error);
          toast({
            title: 'Share failed',
            description: 'Failed to share to Nostr. Please try again.',
            variant: 'destructive',
          });
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          disabled={disabled}
          className="glass dark:glass-dark hover:bg-orange-500/10 font-bold border border-orange-500/20 hover:border-orange-500/40 transition-all duration-300 group"
        >
          <svg className="h-4 w-4 sm:mr-2 transition-transform group-hover:scale-110" viewBox="0 0 875 875" fill="currentColor">
            <path d="M0 437.5C0 196.5 196.5 0 437.5 0S875 196.5 875 437.5 678.5 875 437.5 875 0 678.5 0 437.5z" fill="currentColor"/>
            <path d="M652.5 282.5c-17.5-17.5-17.5-17.5-35-17.5h-35c-17.5 0-35 0-52.5 17.5l-35 35-35-35c-17.5-17.5-35-17.5-52.5-17.5h-35c-17.5 0-35 0-35 17.5l-17.5 17.5c-17.5 17.5-17.5 35-17.5 52.5v35c0 17.5 0 35 17.5 52.5l175 175c17.5 17.5 35 17.5 52.5 0l175-175c17.5-17.5 17.5-35 17.5-52.5v-35c0-17.5 0-35-17.5-52.5l-17.5-17.5z" fill="hsl(var(--background))"/>
          </svg>
          <span className="hidden sm:inline">Share to Nostr</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="glass dark:glass-dark border border-orange-500/30 shadow-2xl shadow-orange-500/10 max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-2xl font-black flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl blur-lg opacity-50 animate-breathe" />
              <div className="relative bg-gradient-to-br from-orange-500 to-amber-500 p-2 rounded-xl">
                <Send className="h-5 w-5 text-white" />
              </div>
            </div>
            <span className="bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 bg-clip-text text-transparent">
              Share to Nostr
            </span>
          </DialogTitle>
          <DialogDescription className="text-base font-medium text-foreground/60">
            Customize your message before sharing this poll with your followers
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label htmlFor="content" className="text-base font-bold">
              Your Note
            </Label>
            <Textarea
              id="content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="min-h-[200px] text-base glass dark:glass-dark border border-orange-500/20 focus:border-orange-500/60 focus:ring-orange-500/20 font-medium resize-none transition-all duration-300"
              placeholder="Write your message..."
            />
            <p className="text-xs text-foreground/50 font-medium">
              {content.length}/2000 characters
            </p>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              className="flex-1 glass dark:glass-dark border border-orange-500/20 hover:bg-orange-500/10 hover:border-orange-500/40 font-bold transition-all duration-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handlePublish}
              disabled={isPending || !user || content.trim().length === 0}
              className="flex-1 bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 hover:shadow-xl hover:shadow-orange-500/30 transition-all duration-300 border-0 font-black text-white relative overflow-hidden group"
            >
              {/* Animated background */}
              <div className="absolute inset-0 bg-gradient-to-r from-orange-600 via-amber-600 to-orange-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              
              {isPending ? (
                <span className="flex items-center gap-2 relative z-10">
                  <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Publishing...
                </span>
              ) : (
                <span className="flex items-center gap-2 relative z-10">
                  <Zap className="h-4 w-4" />
                  Publish to Nostr
                </span>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
